import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ScrollableX } from "../../styles/shared/card";
import { ActiveArea } from "../../styles/shared/page";
import TextInput from "../../components/controls/text-input";
import {
  ICON_ID,
} from "../../components/icons/google-icon";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  fetchProducts,
  resetProductPage,
  searchProducts,
  setLoadingProduct,
  setSearchString,
} from "../../store/features/product/product-slice";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import ProductList from "../../components/controls/product-list";
import { blurAnimation } from "../../styles/shared/loading";
import LoadingSpinner from "../../components/controls/loading-spinner";
import { TableContainer } from "../../styles/shared/table";
import useScreenSize from "../../hooks/useScreenSize";
import { fetchBasketItems } from "../../store/features/ordering/basket/basket-slice";
import { closeDrawer, openDrawer } from "../../store/features/drawer/drawer-slice";
import { DrawerID } from "../../constants/drawer-constants";
import { useParams } from "react-router-dom";

export default function ProductSelection() {
  const { subDepartmentId } = useParams();
  const products = useAppSelector((state) => state.product);
  const basket = useAppSelector((state) => state.basket);
  const branch = useAppSelector((state) => state.branch).selectedBranch;
  const dispatch = useAppDispatch();

  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const loadingRowRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useScreenSize();
  const [columnCount, setColumnCount] = useState(10);

  useEffect(() => {
    if (basket.basket) {
      dispatch(fetchBasketItems({
        basketId: basket.basket!.id, fetchParams: {
          reset: true
        }
      }));
    }
  }, [basket.basket]);

  const loadProducts = async (subDepartmentId: number) => {
    await dispatch(fetchProducts(subDepartmentId));
  }

  useEffect(() => {
    if (subDepartmentId) {
      loadProducts(Number(subDepartmentId));
    }

    return () => {
      dispatch(resetProductPage())
    }
  }, [subDepartmentId])

  useInfiniteScroll({
    dataLength: products.products?.products.length || 0,
    totalCount: products.products?.count || 0,
    fetchData: () => loadProducts(Number(subDepartmentId)),
    loading: products.products?.loading || false,
    loadingRowRef,
  });

  const handleProductClick = (
    id: number,
    productName: string
  ) => {
    dispatch(
      openDrawer({
        id: DrawerID.Product,
        anchor: "right",

        data: {
          headerText: productName,
          productId: id,
          branchId: branch?.id,
          basketCheck: true,
          addToBasketCallback: () => dispatch(closeDrawer()),
          skipText: "Close",
        },
      })
    );
  };

  const searchProductOnChange = (value: string) => {
    if (value !== products.searchString) {
      dispatch(setSearchString(value));
    }
  };

  useEffect(() => {
    if (products.searchString.length > 2) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const id = setTimeout(() => {
        dispatch(
          searchProducts({
            subDepartmentId: Number(subDepartmentId),
            searchText: products.searchString,
          })
        );
      }, 500);
      setTimeoutId(id);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [products.searchString]);


  useEffect(() => {
    if (isMobile) {
      setColumnCount(2);
    }
    else if (isTablet) {
      setColumnCount(5);
    }
    else {
      setColumnCount(10);
    }
  }, [isMobile, isTablet])
  return (
    <TableContainer>

      <ActiveArea>
        <StickyContent>
          <ScrollableX>
            <SearchInput>
              <TextInput
                id="btnSearchProducts"
                label="Search Products"
                value={products?.searchString}
                onChange={(event) => searchProductOnChange(event.target.value)}
                iconID={ICON_ID.Search}
                autoBlurAfterChange={true}
              ></TextInput>
            </SearchInput>

            {products.searchLoading && <LoadingSpinner></LoadingSpinner>}
          </ScrollableX>
        </StickyContent>
        <DataContainer>
          <ScrollableX>
            {products.searchResults && (
              <Category>
                <CategoryTitle>
                  {" "}
                  Search Results ({products.searchResults.count})
                </CategoryTitle>

                {products.searchResults.count === 0 ? (
                  <EmptySearch>
                    No products found matching "{products.searchString}"
                  </EmptySearch>
                ) : (
                  <ProductList
                    products={products.searchResults}
                    onClick={handleProductClick}
                    selectedProductIds={basket?.basketItems?.items.map((x) => x.productId)}
                    columns={columnCount}
                  ></ProductList>
                )}
              </Category>
            )}
            <ProductList
              products={products.products}
              onClick={handleProductClick}
              selectedProductIds={
                basket?.basketItems?.items?.map((x) => x.productId) ?? []
              }
              columns={columnCount}
            />
          </ScrollableX>
        </DataContainer>
      </ActiveArea>

    </TableContainer>
  );
}

const DataContainer = styled.div`
  background: ${(props) => props.theme.application.background};
`

const StickyContent = styled.div`
    position: sticky;
    top: 0; /* or adjust to match any header height */
    background:white;
    z-index:3;
`;
const SearchInput = styled.div`
  margin-bottom: 20px;
  padding: 0px 30px;
`;

const EmptySearch = styled.div`
  padding: 10px;
`;

const Category = styled.div``;

const CategoryTitle = styled.div`
  cursor: pointer;
  font-size: 1.8rem;
  font-weight: 600;
  padding: 10px;
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.application.border};
  span {
    float: right;
    margin-top: 5px;
  }
`;

const CategoryPlaceholder = styled.div`
  height: 44px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: ${(props) => props.theme.application.placeholder};
  padding: 10px;
  animation: ${blurAnimation} 0.5s infinite;
`;

// Add new styled components
const SubDepartmentTabs = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 30px;
  border-bottom: 1px solid ${(props) => props.theme.application.border};
  overflow-x: auto;

`;

const SubDepartmentTab = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  font-size:1.5rem;
  font-weight:600;
  border-radius: 6px;
  border: none;
  background: ${(props) =>
    props.active
      ? props.theme.application.scheme.primary
      : props.theme.application.background};
  color: ${(props) =>
    props.active
      ? props.theme.application.scheme.contrast
      : props.theme.application.text};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${(props) =>
    props.active
      ? props.theme.application.primary
      : props.theme.application.hover};
  }
`;